import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Invitations Tab — admin moderation of partner/client invites
const InvitationsTab = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, accepted: 0, rejected: 0, cancelled: 0, total: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [list, stat] = await Promise.all([
        axios.get(`${API}/admin/invitations?status=${filter}`, { withCredentials: true }),
        axios.get(`${API}/admin/invitations/stats`, { withCredentials: true }),
      ]);
      setItems(list.data || []);
      setStats(stat.data || {});
    } catch (e) {
      toast.error('Erreur de chargement des invitations');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = items.filter(it => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      it.recipient_email.toLowerCase().includes(q) ||
      (it.sender_name || '').toLowerCase().includes(q) ||
      (it.sender_email || '').toLowerCase().includes(q)
    );
  });

  const moderate = async (invId, action) => {
    setBusyId(invId);
    try {
      await axios.post(`${API}/admin/invitations/${invId}/${action}`, {}, { withCredentials: true });
      toast.success(action === 'approve' ? 'Invitation approuvée' : 'Invitation rejetée');
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  const statusColor = (s) => {
    return {
      pending:   'bg-amber-100 text-amber-700 border-amber-200',
      approved:  'bg-emerald-100 text-emerald-700 border-emerald-200',
      accepted:  'bg-blue-100 text-blue-700 border-blue-200',
      rejected:  'bg-red-100 text-red-700 border-red-200',
      cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
    }[s] || 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const fmt = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div data-testid="invitations-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {[
          { key: 'pending',   label: 'En attente', color: 'text-amber-600 bg-amber-50 border-amber-200' },
          { key: 'approved',  label: 'Approuvées', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
          { key: 'accepted',  label: 'Acceptées',  color: 'text-blue-600 bg-blue-50 border-blue-200' },
          { key: 'rejected',  label: 'Rejetées',   color: 'text-red-600 bg-red-50 border-red-200' },
          { key: 'cancelled', label: 'Annulées',   color: 'text-gray-600 bg-gray-50 border-gray-200' },
          { key: 'total',     label: 'Total',      color: 'text-gray-900 bg-white border-gray-200' },
        ].map(s => (
          <div key={s.key} className={`p-3 rounded-xl border ${s.color}`} data-testid={`invite-stat-${s.key}`}>
            <p className="text-2xl font-semibold">{stats[s.key] || 0}</p>
            <p className="text-[10px] uppercase tracking-widest font-bold mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {['pending', 'approved', 'accepted', 'rejected', 'cancelled', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                filter === s
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`invite-filter-${s}`}
            >
              {s === 'all' ? 'Tous' : s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== 'all' && stats[s] != null && <span className="ml-1.5 text-[10px] opacity-75">({stats[s]})</span>}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher email…"
            className="pl-9 h-9 text-sm"
            data-testid="invite-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm" data-testid="invite-empty">
          {search ? 'Aucun résultat.' : 'Aucune invitation pour ce filtre.'}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Destinataire</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Envoyée par</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Rôle</th>
                <th className="text-left px-4 py-3">Statut</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Date</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.invitation_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`invite-row-${inv.invitation_id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{inv.recipient_email}</p>
                    {inv.message && <p className="text-xs text-gray-500 italic mt-0.5 line-clamp-1 max-w-xs">"{inv.message}"</p>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <p className="text-xs text-gray-700">{inv.sender_name}</p>
                    <p className="text-[11px] text-gray-400">{inv.sender_email}</p>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="text-xs text-gray-600">{inv.role_label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${statusColor(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-500">{fmt(inv.created_at)}</td>
                  <td className="px-4 py-3">
                    {inv.status === 'pending' ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === inv.invitation_id}
                          onClick={() => moderate(inv.invitation_id, 'reject')}
                          className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`invite-reject-${inv.invitation_id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Rejeter
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyId === inv.invitation_id}
                          onClick={() => moderate(inv.invitation_id, 'approve')}
                          className="text-xs h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`invite-approve-${inv.invitation_id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                          Approuver
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[11px] text-gray-400 italic">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvitationsTab;
